/* eslint-disable no-param-reassign */
/* eslint-disable object-curly-newline */
import React, { useState, useEffect, Fragment } from 'react';
import { withRouter } from 'react-router-dom';

import getUsersList from '../../../api/graphql/queries/getUsersList';
import getUsersExport from '../../../api/graphql/queries/getUsersExport';
import userDeleteAccount from '../../../api/graphql/mutations/userDeleteAccount';
import userEdit from '../../../api/graphql/mutations/userEdit';
import useSharedFilters from '../../../hooks/useSharedFilters';

import UsersTable from './UsersTable';
import Toolbar from './UsersToolbar';
import UsersForm from './UsersForm';
import UsersInfoPopup from './UsersInfoPopup';
import Modal from '../../Modal/Modal';
import LoadIcon from '../../LoadIcon';

// Check if current deployment is RDL
const isRdl = process.env.APP_NAME === 'rdl';

const UsersContainer = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOn, setIsCreateModalOn] = useState(false);
  const [isDisplayModalOn, setIsDisplayModalOn] = useState(false);
  const [isModifyModalOn, setIsModifyModalOn] = useState(false);
  const [isDeleteModalOn, setIsDeleteModalOn] = useState(false);
  const [isInfoModalOn, setIsInfoModalOn] = useState(false);
  const [formOutput, setFormOutput] = useState({});
  const [currItemData, setCurrItemData] = useState([]);
  const [toggleDeleteInfoScreen, setToggleDeleteInfoScreen] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [downloadingAllInProgress, setDownloadingAllInProgress] = useState(false);
  const [downloadingInProgress, setDownloadingInProgress] = useState(false);
  const [downloadingMagazineInProgress, setDownloadingMagazineInProgress] = useState(false);
  const [reloadData, setReloadData] = useState(false);

  // Shared filters (synced with URL for persistence between Users and Subscriptions tabs)
  const {
    email, setEmail,
    name, setName,
    startDate, setStartDate,
    finishDate, setFinishDate,
  } = useSharedFilters();

  // Non-shared filtering items:
  const [active, setActive] = useState(true);
  const [idNumber, setIdNumber] = useState(null);
  const [socialnetwork, setSocialnetwork] = useState(null);
  const [verified, setVerified] = useState(null);
  const [role, setRole] = useState(null);
  // RDL-only filters
  const [hasAddress, setHasAddress] = useState(null);
  const [sendMagazine, setSendMagazine] = useState(null);
  const [duplicatesOnly, setDuplicatesOnly] = useState(null);


  // Sorting items:
  const [sorting, setSorting] = useState(['createdAt', -1]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [previousPage, setPreviousPage] = useState(0);
  const [avoidEffect, setAvoidEffect] = useState(false);

  const deleteUser = async (event) => {
    // const { currentTarget: { email } } = event;
    const { currentTarget: { id, attributes: { 'data-email': { value: email } = {} } } } = event;
    if (window.confirm('Delete user? It will also be removed from email subscriptions and CRM\'s')) {
      const res = await userDeleteAccount({ email });
      if (res.isDeleted) {
        setReloadData(new Date());
      } else {
        alert(`Error: ${res.error}`);
      }
    }
  };

  const editUser = async (event) => {
    const { currentTarget: { id } } = event;
    handleClick('modify', id);
    // eslint-disable-next-line no-alert
  };

  const viewUserInfo = async (event) => {
    const { currentTarget: { id } } = event;
    handleClick('info', id);
  };

  // Base columns (always present)
  const baseColumns = [
    { field: '_id', type: 'string', hide: true, isId: true, align: 'left' },
    { field: null, headerName: '', type: 'action', align: 'left', content: ((rowData, idValue) => (<span id={idValue} className="font-icon icon-edit text-20 cursor-pointer" onClick={editUser} title="Edit user" />)) },
    { field: null, headerName: '', type: 'action', align: 'left', content: ((rowData, idValue) => (<span id={idValue} className="font-icon icon-eye text-20 cursor-pointer" onClick={viewUserInfo} title="View info" />)) },
    { field: 'createdAt', headerName: 'Date created', type: 'date', sortable: true, align: 'left' },
    { field: 'email', headerName: 'Email', type: 'string', sortable: true, align: 'left' },
    { field: 'name', headerName: 'Name', type: 'string', sortable: true, align: 'left' },
    { field: 'lastname', headerName: 'Last name', type: 'string', sortable: true, align: 'left' },
    { field: 'idNumber', headerName: 'DNI', type: 'string', sortable: true, align: 'left' },
    { field: 'serverCountry', headerName: 'Country', type: 'string', sortable: true, align: 'left' },
    { field: 'socialnetwork', headerName: 'Social Login', type: 'string', sortable: true, align: 'left' },
    { field: 'verified', headerName: 'Verified', type: 'boolean', sortable: true, align: 'center' },
    { field: 'acceptedConditions', headerName: 'Accept Cond.', type: 'boolean', sortable: true, align: 'center' },
    { field: 'active', headerName: 'Active', type: 'boolean', sortable: true, align: 'center' },
    { field: 'roles', headerName: 'Roles', type: 'array', sortable: true, align: 'left' },
    { field: 'originPost', headerName: 'Origin Post', type: 'string', sortable: true, align: 'left' },
  ];

  // RDL-only columns
  // Magazine column only shown when sendMagazine filter is active (to avoid slow queries)
  const rdlColumns = isRdl ? [
    { field: 'address', headerName: 'Has Address', type: 'hasAddress', sortable: false, align: 'center' },
    ...(sendMagazine !== null ? [{ field: 'eligibleForMagazine', headerName: 'Magazine', type: 'eligibleForMagazine', sortable: false, align: 'center', description: 'Eligible to receive magazine' }] : []),
    ...(duplicatesOnly !== null ? [{ field: 'duplicateScore', headerName: 'Duplicate', type: 'duplicate', sortable: false, align: 'left', description: 'Confidence that this reader has another account, and which one' }] : []),
  ] : [];

  // Trailing columns
  const trailingColumns = [
    { field: 'loggedFrom', headerName: 'Last Seen', type: 'lastSeen', sortable: true, align: 'left' },
    { field: 'loggedFrom', headerName: 'Last Country', type: 'lastCountry', sortable: true, align: 'center' },
    { field: null, headerName: '', type: 'action', align: 'left', content: ((rowData, idValue) => (<span id={idValue} data-email={rowData.email} data-deleted={!!rowData.deleted} className={`font-icon icon-trash text-20 ${rowData.deleted ? 'hidden' : 'text-red'}`} onClick={deleteUser} title="Delete user" />)) },
  ];

  const columns = [...baseColumns, ...rdlColumns, ...trailingColumns];

  // What the table renders, which is deliberately NOT the CSV formatter below.
  //
  // The two have always disagreed and both were correct for their own output: on screen a
  // tick reads better than an X, and the date keeps the comma that the CSV strips to stay
  // inside one field. That difference used to be invisible because UsersTable declared its
  // own formatValue and shadowed the `formatValue` prop this container passes it, so the
  // CSV version was dead code as far as the table was concerned. Folding the table into
  // the shared DataTable makes the prop real, so the on-screen version has to be spelled
  // out here or every tick silently becomes an X.
  const formatCell = ({ column, value, row, idValue }) => {
    const mostRecentSession = sessions => sessions.reduce((latest, current) => (
      new Date(current.lastSeen) > new Date(latest.lastSeen) ? current : latest
    ));

    switch (column.type) {
      case 'date':
        return value ? new Date(parseInt(value, 10)).toLocaleString() : '';
      case 'boolean':
        return value ? '✓' : '';
      case 'array':
        return value ? value.join(', ') : '';
      /* The Duplicate column. The score alone says how sure we are but not of what, so the
       * cell also names the other account — that is the thing support acts on. Reasons stay
       * in the info popup, where there is room for them. */
      case 'duplicate': {
        if (!value) return '';
        if (!row.duplicatePartner) return String(value);
        // Say WHERE the subscription is, not just that one exists: that is the answer
        // support needs, and "10 · other@mail.com (suscrita)" reads as if the other
        // account were the subscribed one when it is this row.
        const here = row.duplicateSubscriptionEmail === row.email;
        const label = here ? 'otra cuenta' : 'suscripción en';
        return `${value} · ${label}: ${row.duplicatePartner}`;
      }
      case 'action':
        return column.content(row, idValue);
      case 'loginCount':
        return value && Array.isArray(value) ? value.length : 0;
      case 'lastSeen':
        if (value && Array.isArray(value) && value.length > 0) {
          return new Date(mostRecentSession(value).lastSeen).toLocaleString();
        }
        return '';
      case 'lastCountry':
        if (value && Array.isArray(value) && value.length > 0) {
          return mostRecentSession(value).CountryIp || '';
        }
        return '';
      case 'hasAddress':
        return value && value.street ? '✓' : '';
      case 'eligibleForMagazine':
        return value ? '✓' : '';
      case 'string':
      default:
        return value;
    }
  };

  // Feeds the CSV export only.
  const formatValue = (column, value, csv = false) => {
    if (!value && column.type !== 'hasAddress' && column.type !== 'eligibleForMagazine') {
      return '';
    }

    switch (column.type) {
      case 'date':
        return new Date(parseInt(value, 10)).toLocaleString().replace(',', '');
      case 'boolean':
        return 'X';
      case 'array':
        return value.join(', ');
      case 'loginCount':
        return value && Array.isArray(value) ? value.length : 0;
      case 'lastSeen':
        if (value && Array.isArray(value) && value.length > 0) {
          const mostRecent = value.reduce((latest, current) => {
            const latestDate = new Date(latest.lastSeen);
            const currentDate = new Date(current.lastSeen);
            return currentDate > latestDate ? current : latest;
          });
          return new Date(mostRecent.lastSeen).toLocaleString().replace(',', '');
        }
        return '';
      case 'lastCountry':
        if (value && Array.isArray(value) && value.length > 0) {
          const mostRecent = value.reduce((latest, current) => {
            const latestDate = new Date(latest.lastSeen);
            const currentDate = new Date(current.lastSeen);
            return currentDate > latestDate ? current : latest;
          });
          return mostRecent.CountryIp || '';
        }
        return '';
      case 'hasAddress':
        return value && value.street ? 'X' : '';
      case 'eligibleForMagazine':
        // This will be computed in the backend or here based on subscription data
        return csv ? (value ? 'Yes' : 'No') : (value ? 'X' : '');
      case 'string':
      default:
        return value;
    }
  };

  useEffect(() => {
    if (avoidEffect) {
      return;
    }
    let from = page * rowsPerPage;
    let to = (page + 1) * rowsPerPage;
    if (previousPage === page) {
      setAvoidEffect(true);
      setPage(0);
      from = 0;
      to = rowsPerPage;
    } else {
      from = page * rowsPerPage;
      to = (page + 1) * rowsPerPage;
      setPreviousPage(page);
    }
    console.log('Calling getUsersList()');
    getUsersList({
      active,
      email,
      name,
      idNumber,
      socialnetwork,
      verified,
      role,
      startDate,
      finishDate,
      from,
      to,
      sortingField: sorting[0],
      sortingType: sorting[1],
      // RDL-only filters - only include eligibility when filtering by sendMagazine
      ...(isRdl && hasAddress !== null && { hasAddress }),
      ...(isRdl && sendMagazine !== null && { sendMagazine, includeEligibility: true }),
      ...(isRdl && duplicatesOnly !== null && { duplicatesOnly }),
    }).then((usersDataObject) => {
      if (usersDataObject) {
        if (usersDataObject.isAuth) {
          setTotal(usersDataObject.total);
          setQuery(usersDataObject.query);
          setFilteredData(usersDataObject.users);
          setIsLoading(false);
        } else {
          console.log('getUsersList failed!');
        }
      } else {
        console.log('usersData = null');
      }
      setAvoidEffect(false);
    });
  }, [
    active,
    email,
    name,
    idNumber,
    socialnetwork,
    verified,
    role,
    startDate,
    finishDate,
    page,
    rowsPerPage,
    sorting,
    reloadData,
    hasAddress,
    sendMagazine,
    duplicatesOnly,
  ]);

  // const updateSearch = (val) => {
  //   setFilterText(val);
  //   setFilteredData(initialItems.filter(item => item.email.toLowerCase().includes(val.toLowerCase())));
  // };

  const toggleCreateModal = () => {
    setIsCreateModalOn(!isCreateModalOn);
  };

  const toggleDisplayModal = (item) => {
    setIsDisplayModalOn(!isDisplayModalOn);
    setCurrItemData(filteredData.find(el => el._id === item));
  };

  const toggleModifyModal = (item) => {
    setIsModifyModalOn(!isModifyModalOn);
    setCurrItemData(filteredData.find(el => el._id === item));
  };

  const toggleDeleteModal = (item) => {
    setIsDeleteModalOn(!isDeleteModalOn);
    setCurrItemData(filteredData.find(el => el._id === item));
  };

  const toggleInfoModal = (item) => {
    setIsInfoModalOn(!isInfoModalOn);
    setCurrItemData(filteredData.find(el => el._id === item));
  };

  // Helper function to reload users data
  const reloadUsersData = async () => {
    const from = page * rowsPerPage;
    const to = (page + 1) * rowsPerPage;

    const usersDataObject = await getUsersList({
      active,
      email,
      name,
      idNumber,
      socialnetwork,
      verified,
      role,
      startDate,
      finishDate,
      from,
      to,
      sortingField: sorting[0],
      sortingType: sorting[1],
      ...(isRdl && hasAddress !== null && { hasAddress }),
      ...(isRdl && sendMagazine !== null && { sendMagazine, includeEligibility: true }),
      ...(isRdl && duplicatesOnly !== null && { duplicatesOnly }),
    });

    if (usersDataObject && usersDataObject.isAuth) {
      setTotal(usersDataObject.total);
      setQuery(usersDataObject.query);
      setFilteredData(usersDataObject.users);
    }

    return usersDataObject;
  };

  const formOutputHandler = async (value) => {
    setFormOutput(value);

    // Call the userEdit mutation to update the user
    if (currItemData && currItemData._id) {
      try {
        console.log('formOutputHandler - value received:', value);
        console.log('formOutputHandler - currItemData:', currItemData);
        console.log('formOutputHandler - roles to send:', value.roles);

        const result = await userEdit({
          id: currItemData._id,
          email: value.email,
          name: value.name,
          lastname: value.lastname,
          idNumber: value.idNumber,
          roles: value.roles,
          // Address fields
          addressName: value.addressName,
          addressLastname: value.addressLastname,
          addressStreet: value.addressStreet,
          addressStreetNumber: value.addressStreetNumber,
          addressApartment: value.addressApartment,
          addressCity: value.addressCity,
          addressProvince: value.addressProvince,
          addressPostalCode: value.addressPostalCode,
          addressCountry: value.addressCountry,
        });

        console.log('formOutputHandler - userEdit result:', result);

        if (result.isEdited) {
          // Close the modal first
          setIsModifyModalOn(false);
          // Reload the data and wait for it to complete before showing success message
          await reloadUsersData();
          alert('User updated successfully!');
        } else if (result.error) {
          alert(`Error updating user: ${result.error}`);
        } else if (!result.isAuth) {
          alert('Authentication failed. Please check your permissions.');
        } else {
          alert('User was not updated. Please try again.');
        }
      } catch (error) {
        console.error('Error updating user:', error);
        alert(`Error updating user: ${error.message}`);
      }
    }
  };

  const handleClick = (action, item) => {
    switch (action) {
      case 'create':
        toggleCreateModal();
        break;
      case 'display':
        toggleDisplayModal(item);
        break;
      case 'modify':
        toggleModifyModal(item);
        break;
      case 'delete':
        toggleDeleteModal(item);
        break;
      case 'info':
        toggleInfoModal(item);
        break;
      case 'closeDeleteItem':
        setToggleDeleteInfoScreen(!toggleDeleteInfoScreen);
        break;
      default:
        console.log('wrong action!');
        break;
    }
  };

  // Quote every cell that needs it: names, origin posts and role lists contain commas, and
  // unquoted they shifted the rest of the row into the wrong columns.
  const csvCell = (cell) => {
    const text = cell === null || cell === undefined ? '' : String(cell);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };

  const makeCsv = (rawData) => {
    const visibleColumns = columns.filter(column => !column.hide);
    const lines = [visibleColumns.map(column => csvCell(column.headerName)).join(',')];
    rawData.forEach((user) => {
      lines.push(visibleColumns.map(column => csvCell(formatValue(column, user[column.field], true))).join(','));
    });
    return lines.join('\n');
  };

  /* Fetches every reader matching `filters` in chunks and saves them as users.csv. Chunks are
   * kept small and lean (see getUsersExport): the old full query was ~14 MB per 5,000 users,
   * Lambda rejected it, and with no error handling the spinner simply never stopped. */
  const downloadCsv = async (filters, setInProgress) => {
    const chunkSize = 2500; // ~1.4 MB per chunk on 2026-10-08, well under the 6 MB cap
    setInProgress(true);
    try {
      const { total: totalUsers } = await getUsersExport({ ...filters, from: 0, to: 0 });

      const chunks = await Promise.all(
        Array.from({ length: Math.ceil(totalUsers / chunkSize) }, (_, chunk) => getUsersExport({
          ...filters,
          sortingField: sorting[0],
          sortingType: sorting[1],
          from: chunk * chunkSize,
          to: chunkSize,
          getTotal: false,
        })),
      );
      const rawData = [];
      chunks.forEach(chunk => rawData.push(...chunk.users));

      const element = document.createElement('a');
      const file = new Blob([makeCsv(rawData)], { type: 'text/csv;charset=utf-8;' });
      element.href = URL.createObjectURL(file);
      element.download = 'users.csv';
      document.body.appendChild(element); // Required for this to work in FireFox
      element.click();
      document.body.removeChild(element);
    } catch (error) {
      console.error('Users CSV download failed:', error);
      alert(`Download failed: ${error.message}`);
    } finally {
      setInProgress(false);
    }
  };

  const handleDownload = () => downloadCsv({
    active,
    email,
    name,
    idNumber,
    socialnetwork,
    verified,
    role,
    startDate,
    finishDate,
    ...(isRdl && hasAddress !== null && { hasAddress }),
    ...(isRdl && sendMagazine !== null && { sendMagazine, includeEligibility: true }),
    ...(isRdl && duplicatesOnly !== null && { duplicatesOnly }),
  }, setDownloadingInProgress);

  const handleDownloadAll = () => downloadCsv({}, setDownloadingAllInProgress);

  // RDL-only: Download magazine recipients CSV
  const handleDownloadMagazine = async () => {
    setDownloadingMagazineInProgress(true);
    try {
      // Import the getMagazineRecipients function (default export)
      const getMagazineRecipientsModule = await import('../../../api/graphql/queries/getMagazineRecipients');
      const getMagazineRecipients = getMagazineRecipientsModule.default;
      const result = await getMagazineRecipients();

      if (result && result.downloadUrl) {
        // Use an anchor element to trigger download without popup blocker
        const element = document.createElement('a');
        element.href = result.downloadUrl;
        element.download = 'magazine-recipients.csv';
        element.target = '_blank';
        element.rel = 'noopener noreferrer';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
      } else if (result && result.error) {
        alert(`Error: ${result.error}`);
      } else {
        alert('Failed to generate magazine recipients CSV');
      }
    } catch (error) {
      console.error('Error downloading magazine recipients:', error);
      alert(`Error: ${error.message}`);
    }
    setDownloadingMagazineInProgress(false);
  };

  return (
    <Fragment>
      <div className="w-full bg-white">
        <div className="flex justify-between mb-4">
          <span className="font-americaMonoBold text-14 uppercase">
            Total:
            {' '}
            {total}
          </span>
        </div>
        {isDisplayModalOn && (
          <Modal title="Display User" handleClick={handleClick} type="display">
            <UsersForm handleClick={handleClick} formInput={currItemData} action="display" />
          </Modal>
        )}
        {isModifyModalOn && (
          <Modal handleClick={handleClick} type="modify">
            <UsersForm handleClick={handleClick} retrieveOutput={formOutputHandler} formInput={currItemData} action="modify" />
          </Modal>
        )}
        {isInfoModalOn && (
          <Modal title="User Info" handleClick={handleClick} type="info">
            <UsersInfoPopup handleClick={handleClick} formInput={currItemData} action="info" />
          </Modal>
        )}
        <div className="mt-4">
          {!isLoading ? (
            <>
              <Toolbar
                active={active}
                setActive={setActive}
                email={email}
                setEmail={setEmail}
                name={name}
                setName={setName}
                setIdNumber={setIdNumber}
                setSocialnetwork={setSocialnetwork}
                setVerified={setVerified}
                setRole={setRole}
                startDate={startDate}
                setStartDate={setStartDate}
                finishDate={finishDate}
                setFinishDate={setFinishDate}
                handleDownload={handleDownload}
                handleDownloadAll={handleDownloadAll}
                downloadingInProgress={downloadingInProgress}
                downloadingAllInProgress={downloadingAllInProgress}
                // RDL-only props
                isRdl={isRdl}
                hasAddress={hasAddress}
                setHasAddress={setHasAddress}
                sendMagazine={sendMagazine}
                setSendMagazine={setSendMagazine}
                duplicatesOnly={duplicatesOnly}
                setDuplicatesOnly={setDuplicatesOnly}
                handleDownloadMagazine={handleDownloadMagazine}
                downloadingMagazineInProgress={downloadingMagazineInProgress}
              />
              <UsersTable
                tableData={filteredData}
                columns={columns}
                stickyHeader
                total={total}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                sorting={sorting}
                setSorting={setSorting}
                formatValue={formatCell}
              />
              <div style={{ color: '#fff' }}>
                {query}
              </div>
            </>
          ) : <LoadIcon />}
        </div>
      </div>
    </Fragment>
  );
};

UsersContainer.propTypes = {
};

export default withRouter(UsersContainer);
